/**
 * Alert Muting Card Component
 *
 * Allows users to mute role-based alert notifications globally or per-space.
 * Integrates into the main Settings page.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  BellOff,
  CheckCircle,
  Info,
  Calendar,
  CheckSquare,
  MapPin,
  Award,
  Users,
  Shield,
  Grid,
  CalendarDays,
  MessageSquare,
  FileText
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { ALERT_DEFINITIONS } from '@/config/alertDefinitions';

// Map icon strings to Lucide components
const ICON_MAP = {
  Calendar: Calendar,
  CheckSquare: CheckSquare,
  MapPin: MapPin,
  Award: Award,
  Users: Users,
  Shield: Shield,
  Grid: Grid,
  Grid3x3: Grid, // Matrix uses Grid3x3 but we map it to Grid
  CalendarDays: CalendarDays,
  MessageSquare: MessageSquare,
  FileText: FileText
};

export function AlertMutingCard() {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [globallyMutedTools, setGloballyMutedTools] = useState([]);
  const [spaceMutedTools, setSpaceMutedTools] = useState({});
  const [userSpaces, setUserSpaces] = useState([]);

  useEffect(() => {
    loadSettings();
  }, [userProfile?.id]);

  const loadSettings = async () => {
    if (!userProfile?.id) return;

    try {
      setLoading(true);

      // Load alert muting preferences
      const { data: mutingData, error: mutingError } = await supabase
        .from('user_alert_muting')
        .select('globally_muted_tools, space_muted_tools')
        .eq('user_id', userProfile.id)
        .single();

      if (mutingError && mutingError.code !== 'PGRST116') {
        throw mutingError;
      }

      setGloballyMutedTools(mutingData?.globally_muted_tools || []);
      setSpaceMutedTools(mutingData?.space_muted_tools || {});

      // Load user's spaces
      const { data: spacesData, error: spacesError } = await supabase
        .from('team_members')
        .select(`
          project_id,
          projects:project_id (
            id,
            name
          )
        `)
        .eq('user_id', userProfile.id);

      if (spacesError) throw spacesError;

      const spaces = spacesData
        ?.map(tm => tm.projects)
        .filter(Boolean) || [];

      setUserSpaces(spaces);

    } catch (error) {
      console.error('Error loading alert muting settings:', error);
      toast.error('Failed to load alert muting settings');
    } finally {
      setLoading(false);
    }
  };

  const handleGlobalMuteToggle = (toolId) => {
    setGloballyMutedTools(prev => {
      if (prev.includes(toolId)) {
        return prev.filter(id => id !== toolId);
      } else {
        return [...prev, toolId];
      }
    });
    setHasChanges(true);
  };

  const handleSpaceMuteToggle = (spaceId, toolId) => {
    setSpaceMutedTools(prev => {
      const spaceMuted = prev[spaceId] || [];
      const newSpaceMuted = spaceMuted.includes(toolId)
        ? spaceMuted.filter(id => id !== toolId)
        : [...spaceMuted, toolId];

      return {
        ...prev,
        [spaceId]: newSpaceMuted
      };
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!userProfile?.id) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_alert_muting')
        .upsert({
          user_id: userProfile.id,
          globally_muted_tools: globallyMutedTools,
          space_muted_tools: spaceMutedTools
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      toast.success('Alert muting preferences saved successfully');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving alert muting settings:', error);
      toast.error('Failed to save alert muting settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellOff className="h-5 w-5" />
            Mute Alert Notifications
          </CardTitle>
          <CardDescription>Loading alert settings...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BellOff className="h-5 w-5" />
          Mute Alert Notifications
        </CardTitle>
        <CardDescription>
          Control which role-based alert notifications you receive
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Info Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <p className="font-medium">About Alert Notifications</p>
              <p className="text-amber-700 mt-1">
                Alerts are role-based notifications configured by your Admin for important system events.
                You can mute specific tools globally or for individual spaces.
              </p>
            </div>
          </div>
        </div>

        {/* Global Muting */}
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-semibold">Global Alert Muting</h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              Mute alerts from these tools across all spaces
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.entries(ALERT_DEFINITIONS).map(([toolId, toolDef]) => {
              const Icon = ICON_MAP[toolDef.icon];
              const isMuted = globallyMutedTools.includes(toolId);

              return (
                <div
                  key={toolId}
                  className="flex items-center justify-between p-2 rounded-lg border hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {Icon && <Icon className="h-4 w-4 text-indigo-600" />}
                    <span className="text-sm font-medium">{toolDef.name}</span>
                  </div>
                  <Checkbox
                    checked={isMuted}
                    onCheckedChange={() => handleGlobalMuteToggle(toolId)}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Per-Space Muting */}
        {userSpaces.length > 0 && (
          <>
            <Separator />
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold">Space-Specific Alert Muting</h4>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Mute alerts from specific tools in individual spaces
                </p>
              </div>

              {userSpaces.map(space => (
                <div key={space.id} className="space-y-2">
                  <h5 className="text-sm font-medium text-muted-foreground">{space.name}</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-3">
                    {Object.entries(ALERT_DEFINITIONS).map(([toolId, toolDef]) => {
                      const Icon = ICON_MAP[toolDef.icon];
                      const isMuted = spaceMutedTools[space.id]?.includes(toolId) || false;
                      const isGloballyMuted = globallyMutedTools.includes(toolId);

                      return (
                        <div
                          key={toolId}
                          className="flex items-center justify-between p-2 rounded-lg border hover:bg-muted/30 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            {Icon && <Icon className="h-3.5 w-3.5 text-indigo-600" />}
                            <span className="text-xs font-medium">{toolDef.name}</span>
                            {isGloballyMuted && (
                              <Badge variant="outline" className="text-xs py-0 px-1 h-4">
                                Globally Muted
                              </Badge>
                            )}
                          </div>
                          <Checkbox
                            checked={isMuted}
                            onCheckedChange={() => handleSpaceMuteToggle(space.id, toolId)}
                            disabled={isGloballyMuted}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Save Button */}
        {hasChanges && (
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <div className="h-2 w-2 bg-amber-500 rounded-full animate-pulse"></div>
              <span>You have unsaved changes</span>
            </div>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Save Alert Preferences
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
